import { Outlet, useNavigate, useParams } from 'react-router-dom'

import { TeamDocument, type TeamQuery } from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { useQuery } from 'modules/core/hooks/useQuery'
import i18n from 'modules/core/lib/i18n'

import {
  ResponsivePage,
  type ResponsivePageActionsInterface,
} from 'modules/layout/components/ResponsivePage'
import TeamDetail from 'modules/teams/components/Detail'
import EditModal from 'modules/teams/components/EditModal'
import TeamsMembersModal from 'modules/teams/components/MembersModal'
import RemoveTeamModal from 'modules/teams/components/RemoveTeamModal'
import type { ModalsState } from 'modules/teams/hooks/useModals'
import useModals from 'modules/teams/hooks/useModals'
import type { ITeam } from 'modules/teams/store/models/Team'

type TeamMembership = NonNullable<NonNullable<TeamQuery['teams']['team']>['memberships']>[number]

interface ContentProps {
  team: ITeam | null | undefined
  canManageTeams: boolean
  modals: ModalsState
  memberships: TeamMembership[]
  hasDevJobCatalogTree: boolean
}

const Content = ({
  team,
  canManageTeams,
  modals,
  memberships,
  hasDevJobCatalogTree,
}: ContentProps) => {
  if (!team) return null

  return (
    <>
      {modals.isAddMemberOpen && (
        <TeamsMembersModal team={team} onClose={modals.onCloseAddMember} />
      )}

      {modals.isEditOpen && <EditModal team={team} onClose={modals.onCloseEdit} />}

      {modals.isRemoveOpen && <RemoveTeamModal team={team} onClose={modals.onCloseRemove} />}

      <TeamDetail
        readonly={!canManageTeams}
        team={team}
        memberships={memberships}
        hasDevJobCatalogTree={hasDevJobCatalogTree}
      />
    </>
  )
}

const TeamDetailPage = () => {
  const { id } = useParams<{ id: string }>()
  const { company } = useCurrentSession()
  const { data, loading: isLoadingTeam } = useQuery(TeamDocument, {
    variables: {
      teamId: Number(id),
      companyId: company.id,
    },
    fetchPolicy: 'cache-first',
  })
  const navigate = useNavigate()
  const { navigation } = useNavigation()
  const modals = useModals()
  const team = data?.teams.team
  const canManageTeams = data?.teams.team?._policies?.manage?.can ?? false
  const hasLegacyJobCatalog =
    data?.features.featuresConnection.nodes.find((feature) => feature.name === 'legacy_job_catalog')
      ?.enabled ?? false
  const hasDevJobCatalogTree = !hasLegacyJobCatalog
  const memberships = team?.memberships ?? []

  const actions: ResponsivePageActionsInterface = {
    primary: [
      {
        icon: 'add',
        label: i18n.t('teams.members_modal.title'),
        onClick: modals.onOpenAddMember,
        variant: 'primary',
      },
    ],
    secondary: [
      { onClick: modals.onOpenEdit, title: i18n.t('teams.edit') },
      {
        onClick: modals.onOpenRemove,
        title: i18n.t('teams.delete'),
        danger: true,
      },
    ],
  }

  // todo: passs down title and description error to responsive page
  // title: i18n.t('errors.page_not_found.title'),
  // description: i18n.t('errors.page_not_found.description')

  return (
    <ResponsivePage.Body
      showHeader={false}
      actions={canManageTeams ? actions : undefined}
      loading={isLoadingTeam}
      error={!team}
      onActionClick={(key) => {
        if (key === 'add-team') {
          team && navigate(navigation.employees.teams.id.addMembers.fullPath({ id: team.id }))
        } else if (key === 'edit-team') {
          team && navigate(navigation.employees.teams.id.edit.fullPath({ id: team.id }))
        } else if (key === 'delete-team') {
          team && navigate(navigation.employees.teams.id.delete.fullPath({ id: team.id }))
        }
      }}
    >
      <Content
        canManageTeams={canManageTeams}
        hasDevJobCatalogTree={hasDevJobCatalogTree}
        team={
          team && {
            id: team.id,
            name: team.name,
            description: team.description ?? '',
            avatar: team.avatar ?? '',
          }
        }
        modals={modals}
        memberships={memberships}
      />
      <Outlet />
    </ResponsivePage.Body>
  )
}

export default TeamDetailPage
